import { bunja } from 'bunja';
import { atom } from 'jotai';

import { postUserProfileSocialConnectXApi, postUserProfileSocialDisconnectXApi } from '@flipster/apis/v1/user';
import { TextButton } from '@flipster/react/design-system';
import { snackbar } from '@flipster/react/design-system/snackbar/index';
import { alert } from '@flipster/ui/Alert';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorCode } from '@flipster/utils/error';
import segment from '@flipster/utils/segment';
import { ProfileEvent } from '@flipster/utils/segment/events/profile';

import { profileBunja } from '../../bunja';
import { editProfileBunja } from '../bunja';
import * as oauth from '../shared/oauth';
import { getXRedirectUri } from '../shared/oauth';

export const socialXBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { refetchProfile } = bunja.use(profileBunja);
  const { saveDraft, xConnectionAtom, initialSocialProvidersSegmentAtom } = bunja.use(editProfileBunja);
  const isLoadingAtom = atom(false);
  async function connect() {
    segment.track(ProfileEvent.EditProfilePageConnectXClick, store.get(initialSocialProvidersSegmentAtom));
    saveDraft();
    const pk = await oauth.generateAndStorePkceProofKey();
    const url = oauth.generateXOauthUrl(pk);
    window.location.href = url;
  }
  function disconnect() {
    segment.track(ProfileEvent.EditProfilePageDisconnectXClick);
    alert.show({
      title: 'Disconnect X account',
      message: 'Are you sure you want to disconnect your X account?',
      closeText: 'Cancel',
      confirmText: 'Confirm',
      withClose: true,
      onClose: () => segment.track(ProfileEvent.EditProfilePageDisconnectXCancelClick),
      onConfirm: async () => {
        segment.track(ProfileEvent.EditProfilePageDisconnectXConfirmClick);
        try {
          await postUserProfileSocialDisconnectXApi();
        } finally {
          snackbar.success({ message: 'Your X account is now disconnected' });
          await refetchProfile();
        }
      },
    });
  }
  bunja.effect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('code')) return;
    (async () => {
      try {
        store.set(isLoadingAtom, true);
        const { code, codeVerifier } = oauth.verifyRedirectCallback();
        await postUserProfileSocialConnectXApi({ code, codeVerifier, redirectUri: getXRedirectUri() });
        segment.track(ProfileEvent.EditProfilePageXConnectionStatus, { is_success: true });
        snackbar.success({ message: 'Your X account is now connected' });
        await refetchProfile();
      } catch (error) {
        segment.track(ProfileEvent.EditProfilePageXConnectionStatus, { is_success: false });
        const code = getErrorCode(error);
        if (code == 'api.user.profile.social.alreadyConnectedXAccount') {
          return snackbar.error({ message: 'This X account already in use with another Flipster account' });
        }
        snackbar.error({
          message: "Couldn't connect to X right now",
          button: <TextButton label='Try again' onClick={connect} />,
        });
      } finally {
        store.set(isLoadingAtom, false);
      }
    })();
  });
  return { isLoadingAtom, xConnectionAtom, connect, disconnect };
});
